import { Component } from 'preact';
import {
  GOLD, ACCENT_GOLD, RADIUS,
  CONTENT_BG_DEFAULT, CornerPlating,
} from '../../lib/medievalTheme';
import { pulseUISoundLoop, UI } from '../../utils/arena/uiSounds';
import { isFoilFinish } from '../../utils/sorcery/foil';
import { foilZonesForCard } from '../../utils/sorcery/foilZones';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams';
import FoilPreview from '../FoilPreview';
import { t } from '../../localization/t';

const PORTRAIT_WIDTH_CQW = 14;
const PORTRAIT_HEIGHT_CQW = 19;
const LANDSCAPE_WIDTH_CQW = 19;
const LANDSCAPE_HEIGHT_CQW = 14;
const PANEL_PADDING_CQW = 0.6;
// Wrapper is always landscape-width so the centered ledge below never
// shifts horizontally when the inner panel changes orientation.
const WRAPPER_WIDTH_CQW = LANDSCAPE_WIDTH_CQW;
const LEDGE_WIDTH_CQW = 4.5;
const LEDGE_HEIGHT_CQW = 1.6;
const CORNER_RADIUS_PX = 8;
const TRANSITION = 'cubic-bezier(0.22, 0.9, 0.3, 1)';

// User-driven zoom on the open drawer. Scroll-wheel adjusts; persists
// across sessions via localStorage.
const MIN_SCALE = 0.6;
const MAX_SCALE = 1.8;
const SCALE_PER_WHEEL_PX = 0.0015;

// Horizontal drag position, stored as the wrapper's `right` offset in
// cqw so the position scales with the canvas across different monitor
// sizes (the menu can't disappear off-edge when the user opens the app
// on a smaller display).
const DEFAULT_RIGHT_CQW = 11;
const DRAG_THRESHOLD_PX = 4;

// Drag bounds are computed from the actual panel width. And we always
// use the *wider* (landscape/site) panel as the worst case so a saved
// position stays valid whether the hovered card is portrait or a site.
// Without this, a portrait-mode position near the canvas edge would
// clip the panel the moment a site card comes in.
function computeHorizontalBounds(scale) {
  const widestPanel = LANDSCAPE_WIDTH_CQW * scale;
  // The wrapper is anchored at `right`, the panel is centered inside
  // it. Wrapper center sits at: 100 - rightCqw - WRAPPER_WIDTH_CQW/2.
  // Constrain that center so the panel's full width stays in [0, 100].
  const minRight = (widestPanel - WRAPPER_WIDTH_CQW) / 2;
  const maxRight = 100 - (widestPanel + WRAPPER_WIDTH_CQW) / 2;
  return { minRight, maxRight };
}

const STORAGE_KEY_SCALE = 'valkenhall:cardPreviewDrawer:scale';
const STORAGE_KEY_RIGHT = 'valkenhall:cardPreviewDrawer:rightCqw';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function readSaved(key, fallback, lo, hi) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? clamp(v, lo, hi) : fallback;
  } catch {
    return fallback;
  }
}

function writeSaved(key, value) {
  try { localStorage.setItem(key, String(value)); } catch { /* storage unavailable */ }
}

const PANEL_BASE = '#0e0a06';
const PANEL_BACKGROUND = `${CONTENT_BG_DEFAULT}, ${PANEL_BASE}`;
const PANEL_BORDER = `1px solid ${GOLD} 0.6)`;

// Inset-only set: rim highlights + inner vignette. Stays entirely
// within the panel's bounding box, so when the panel is translated
// off-canvas (closed state) nothing of it bleeds into the visible
// canvas area.
const PANEL_INSET_SHADOW = [
  `inset 1px 0 0 ${GOLD} 0.22)`,
  `inset -1px 0 0 ${GOLD} 0.22)`,
  `inset 0 -1px 0 ${GOLD} 0.22)`,
  'inset 0 0 2.25cqw rgba(0,0,0,0.5)',
].join(', ');

// Open-state shadow: the inset rim plus the outset drop shadow and
// gold halo that gives the panel its floating depth. Only applied
// while open. Otherwise the outset glow would be visible at the
// top of the canvas while the panel itself is hidden above.
const PANEL_OPEN_SHADOW = [
  '0 1.125cqw 3cqw rgba(0,0,0,0.65)',
  `0 0 2.5cqw ${GOLD} 0.12)`,
  PANEL_INSET_SHADOW,
].join(', ');

/**
 * Top-edge drawer that drops down to reveal the last-hovered card.
 * Built for streamers so viewers can read the card the host is
 * discussing.
 *
 * Inert while closed. Does not track the focused-card prop and does
 * not morph. Clicking the ledge activates it: snapshots whatever card
 * is currently hovered, then tracks new hovers until closed again.
 * Click the card image to open the full inspector.
 *
 * Site cards (landscape) get a slot rotated to fit; the wrapper width
 * is held constant so the ledge never shifts horizontally between
 * orientations.
 *
 * Props:
 *   card. { imageUrl, name, cardId, foiling, isSite } or null.
 *   onCardClick. () => void. Fires on image click.
 */
export default class CardPreviewDrawer extends Component {
  state = (() => {
    const scale = readSaved(STORAGE_KEY_SCALE, 1, MIN_SCALE, MAX_SCALE);
    const { minRight, maxRight } = computeHorizontalBounds(scale);
    return {
      open: false,
      // Cached prop snapshot. Only refreshed while open. Keeping the
      // last-shown card around when closing avoids a blank flash during
      // the slide-up.
      displayCard: null,
      scale,
      rightCqw: readSaved(STORAGE_KEY_RIGHT, DEFAULT_RIGHT_CQW, minRight, maxRight),
    };
  })();

  wrapperRef = (el) => { this.wrapperEl = el; };

  // Drag state. Kept off React state since it changes per-pixel and
  // doesn't need to drive re-renders directly (rightCqw does).
  dragMoved = false;
  dragStartX = 0;
  dragStartRight = 0;
  dragCanvasWidthPx = 0;
  suppressNextClick = false;

  componentDidMount() {
    // Attach wheel non-passively so we can preventDefault while the
    // drawer is open (Preact's JSX onWheel attaches passively in some
    // browsers, which blocks preventDefault). Mousedown on the wrapper
    // catches drags on both the panel (when open) and the ledge (always)
    // via bubbling, even though the wrapper itself is pointer-events:none
    // while closed.
    if (this.wrapperEl) {
      this.wrapperEl.addEventListener('wheel', this.handleWheel, { passive: false });
      this.wrapperEl.addEventListener('mousedown', this.handleMouseDown);
    }
  }

  componentWillUnmount() {
    if (this.wrapperEl) {
      this.wrapperEl.removeEventListener('wheel', this.handleWheel);
      this.wrapperEl.removeEventListener('mousedown', this.handleMouseDown);
    }
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  componentDidUpdate(prevProps) {
    if (this.state.open && this.props.card !== prevProps.card) {
      this.setState({ displayCard: this.props.card });
    }
  }

  handleWheel = (e) => {
    if (!this.state.open) return;
    e.preventDefault();
    const delta = -e.deltaY * SCALE_PER_WHEEL_PX;
    const nextScaleHint = clamp(this.state.scale + delta, MIN_SCALE, MAX_SCALE);
    if (nextScaleHint !== this.state.scale) {
      pulseUISoundLoop(UI.SCROLL, { volume: 0.25, holdMs: 150 });
    }
    this.setState(
      (s) => {
        const nextScale = clamp(s.scale + delta, MIN_SCALE, MAX_SCALE);
        if (nextScale === s.scale) return null;
        // Re-clamp the saved position to the new scale's bounds.
        // scaling up shrinks the valid drag range, so a previously
        // valid position can now clip the panel off-canvas.
        const { minRight, maxRight } = computeHorizontalBounds(nextScale);
        const nextRight = clamp(s.rightCqw, minRight, maxRight);
        return { scale: nextScale, rightCqw: nextRight };
      },
      () => {
        writeSaved(STORAGE_KEY_SCALE, this.state.scale);
        writeSaved(STORAGE_KEY_RIGHT, this.state.rightCqw);
      },
    );
  };

  handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const canvas = document.querySelector('.app-canvas');
    const canvasWidthPx = canvas ? canvas.getBoundingClientRect().width : window.innerWidth;
    if (canvasWidthPx <= 0) return;
    // Don't preventDefault here. We want the eventual click to still
    // fire when this turns out to be a click, not a drag. The native
    // image drag is already disabled via draggable={false}.
    this.dragMoved = false;
    this.dragStartX = e.clientX;
    this.dragStartRight = this.state.rightCqw;
    this.dragCanvasWidthPx = canvasWidthPx;
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  };

  handleMouseMove = (e) => {
    const dx = e.clientX - this.dragStartX;
    if (!this.dragMoved) {
      if (Math.abs(dx) <= DRAG_THRESHOLD_PX) return;
      this.dragMoved = true;
    }
    // Moving the cursor right (positive dx) decreases the `right`
    // offset. The menu follows the cursor.
    const deltaCqw = -dx * 100 / this.dragCanvasWidthPx;
    const { minRight, maxRight } = computeHorizontalBounds(this.state.scale);
    const next = clamp(this.dragStartRight + deltaCqw, minRight, maxRight);
    if (next !== this.state.rightCqw) {
      pulseUISoundLoop(UI.SLIDER, { volume: 0.3, holdMs: 120 });
      this.setState({ rightCqw: next });
    }
  };

  handleMouseUp = () => {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    if (this.dragMoved) {
      writeSaved(STORAGE_KEY_RIGHT, this.state.rightCqw);
      this.suppressNextClick = true;
    }
  };

  handleLedgeClick = (e) => {
    e.stopPropagation();
    if (this.suppressNextClick) { this.suppressNextClick = false; return; }
    this.setState((s) => ({
      open: !s.open,
      // On open: snapshot the live prop so the drawer reflects what's
      // currently hovered. On close: keep the last-shown card so the
      // slide-up doesn't blank mid-animation.
      displayCard: !s.open ? this.props.card : s.displayCard,
    }));
  };

  handleImageClick = () => {
    if (this.suppressNextClick) { this.suppressNextClick = false; return; }
    if (this.state.displayCard && this.props.onCardClick) this.props.onCardClick();
  };

  render() {
    const { open, displayCard, scale, rightCqw } = this.state;
    const isSite = !!displayCard?.isSite;
    const isFoil = isFoilFinish(displayCard?.foiling);
    const panelWidth = (isSite ? LANDSCAPE_WIDTH_CQW : PORTRAIT_WIDTH_CQW) * scale;
    const panelHeight = (isSite ? LANDSCAPE_HEIGHT_CQW : PORTRAIT_HEIGHT_CQW) * scale;
    // Wrapper width is intentionally NOT scaled. It stays anchored at
    // its original position so the panel grows symmetrically around a
    // fixed origin instead of drifting left as it widens.

    return (
      <div
        data-card-preview-drawer
        ref={this.wrapperRef}
        style={{
          position: 'fixed',
          top: 0,
          // User-draggable horizontal position (cqw. Scales with the
          // canvas so the menu can't disappear on smaller monitors).
          // Default 11cqw sits just left of the opponent HUD column.
          right: `${rightCqw}cqw`,
          pointerEvents: open ? 'auto' : 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          // 0.25cqw past the panel height keeps the bottom corner
          // plating out of the letterbox on non-16:9 monitors when
          // the drawer is closed.
          transform: `translateY(${open ? '0' : `calc(-${panelHeight}cqw - 0.25cqw)`})`,
          transition: `transform 220ms ${TRANSITION}`,
          zIndex: 1050,
          width: `${WRAPPER_WIDTH_CQW}cqw`,
        }}
      >
        <div
          style={{
            width: `${panelWidth}cqw`,
            height: `${panelHeight}cqw`,
            padding: `${PANEL_PADDING_CQW}cqw`,
            background: PANEL_BACKGROUND,
            backgroundBlendMode: 'overlay, normal',
            border: PANEL_BORDER,
            borderTop: 'none',
            borderRadius: `0 0 ${CORNER_RADIUS_PX}px ${CORNER_RADIUS_PX}px`,
            // Outset shadows only when visible; while closed the
            // panel uses inset-only shadows so its drop shadow can't
            // bleed into the canvas from above the screen edge.
            boxShadow: open ? PANEL_OPEN_SHADOW : PANEL_INSET_SHADOW,
            isolation: 'isolate',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: open ? 'auto' : 'none',
            overflow: 'hidden',
            transition: `width 220ms ${TRANSITION}, height 220ms ${TRANSITION}, box-shadow 220ms ${TRANSITION}`,
          }}
        >
          <CornerPlating position="bottom-left" color={`${GOLD} 0.65)`} />
          <CornerPlating position="bottom-right" color={`${GOLD} 0.65)`} />

          {displayCard?.imageUrl ? (
            <button
              type="button"
              onClick={this.handleImageClick}
              aria-label={displayCard.name ? t('Inspect {name}', { name: displayCard.name }) : t('Inspect card')}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                padding: 0,
                position: 'relative',
              }}
            >
              {isSite ? (
                /* Landscape slot for sites. Image rotated 90°
                   clockwise to fill it. Pre-rotation we set
                   width = inner height, height = inner width so
                   after rotation it lands at full slot dimensions. */
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    borderRadius: RADIUS.cardLandscape,
                    overflow: 'hidden',
                    boxShadow: '0 0.125cqw 0.375cqw rgba(0,0,0,0.5)',
                  }}
                >
                  <img
                    src={displayCard.imageUrl}
                    alt={displayCard.name || ''}
                    draggable={false}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      height: `${panelWidth - PANEL_PADDING_CQW * 2}cqw`,
                      width: `${panelHeight - PANEL_PADDING_CQW * 2}cqw`,
 transform: 'translate(-50%, -50%) rotate(90deg)',
 transformOrigin: 'center center',
 objectFit: 'cover',
 pointerEvents: 'none',
 }}
 />
 {isFoil ? (
 <FoilPreview
 imageUrl={displayCard.imageUrl}
 zones={foilZonesForCard(displayCard)}
 params={DEFAULT_FOIL_PARAMS}
 rotate90
 />
 ) : null}
 </div>
 ) : (
 <div
 style={{
 width: '100%',
 height: '100%',
 position: 'relative',
 borderRadius: RADIUS.card,
 overflow: 'hidden',
 }}
 >
 <img
 src={displayCard.imageUrl}
 alt={displayCard.name || ''}
 draggable={false}
 style={{
 width: '100%',
 height: '100%',
 objectFit: 'contain',
 borderRadius: RADIUS.card,
 boxShadow: '0 0.125cqw 0.375cqw rgba(0,0,0,0.5)',
 pointerEvents: 'none',
 }}
 />
 {isFoil ? (
 <FoilPreview
 imageUrl={displayCard.imageUrl}
 zones={foilZonesForCard(displayCard)}
 params={DEFAULT_FOIL_PARAMS}
 />
 ) : null}
 </div>
 )}
 </button>
 ) : (
 <div
 className="arena-heading"
 style={{
 fontSize: '0.65cqw',
 color: ACCENT_GOLD,
 textAlign: 'center',
 opacity: 0.65,
 }}
 >
 {t('Hover any card')}
 </div>
 )}
 </div>

 <button
 type="button"
 data-tutorial="board-card-preview-ledge"
 onClick={this.handleLedgeClick}
 aria-label={open ? t('Close card preview') : t('Open card preview')}
 aria-pressed={open}
 className="flex items-center justify-center"
 style={{
 width: `${LEDGE_WIDTH_CQW}cqw`,
            height: `${LEDGE_HEIGHT_CQW}cqw`,
            background: PANEL_BACKGROUND,
            backgroundBlendMode: 'overlay, normal',
            border: PANEL_BORDER,
            borderTop: 'none',
            borderRadius: '0 0 0.4cqw 0.4cqw',
            color: ACCENT_GOLD,
            boxShadow: [
              '0 0.5cqw 1.25cqw rgba(0,0,0,0.55)',
              `0 0 1cqw ${GOLD} 0.1)`,
              `inset 1px 0 0 ${GOLD} 0.22)`,
              `inset -1px 0 0 ${GOLD} 0.22)`,
              `inset 0 -1px 0 ${GOLD} 0.22)`,
              'inset 0 0 0.75cqw rgba(0,0,0,0.45)',
            ].join(', '),
            padding: 0,
            isolation: 'isolate',
            position: 'relative',
            marginTop: '-1px',
            zIndex: 2,
            pointerEvents: 'auto',
          }}
        >
          <svg viewBox="0 0 24 24" style={{ width: '0.9cqw', height: '0.9cqw' }} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points={open ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
          </svg>
        </button>
      </div>
    );
  }
}
